/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { X, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import googleMapsLogo from '/images/GoogleMaps_Logo_Gray_4x.png';
import { Button } from '@/components/buttons';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Spinner } from '@/components/spinner';
import { Typography } from '@/components/typography';

/**
 * Represents a structured formatting object for address suggestions
 */
interface StructuredFormatting {
    main_text?: string;
    secondary_text?: string;
}

/**
 * Represents a term in the address suggestion
 */
interface AddressTerm {
    offset?: number;
    value?: string;
}

/**
 * Represents an address suggestion from Google Places API or Experian Aperture
 */
export interface AddressSuggestion {
    /** Full description of the address */
    description?: string;
    /** Google Places place ID, or Experian global_address_key when using Experian */
    place_id?: string;
    /** Experian global_address_key for format API (when using Experian) */
    global_address_key?: string;
    /** Structured formatting with main and secondary text */
    structured_formatting?: StructuredFormatting;
    /** Array of terms in the suggestion */
    terms?: AddressTerm[];
    /** Types of the place */
    types?: string[];
    /** Place prediction object (Google) */
    placePrediction?: object;
    /** Experian confidence, e.g. "Too many matches" */
    confidence?: string;
}

interface AddressSuggestionDropdownProps {
    /** Array of address suggestions to display */
    suggestions?: AddressSuggestion[];
    /** Whether the dropdown is loading */
    isLoading?: boolean;
    /** Callback when close button is clicked */
    onClose: () => void;
    /** Callback when a suggestion is selected */
    onSelectSuggestion: (suggestion: AddressSuggestion) => void;
    /** Whether the dropdown should be visible */
    isVisible?: boolean;
    /** CSS position property for the dropdown */
    position?: 'absolute' | 'relative' | 'fixed';
    /** Optional CSS class name for additional styling */
    className?: string;
    /** Hint message when too many matches (e.g. "Continue typing..." or "Add a house number") */
    hintMessage?: string;
    /** Whether to show Google Maps attribution (default true for Google, false for Experian) */
    showGoogleAttribution?: boolean;
}

/**
 * Address Suggestion Dropdown Component
 * Displays Google-powered address suggestions in a dropdown format
 *
 * @param props - Component props
 * @returns JSX element representing the address suggestion dropdown
 *
 * @example
 * ```tsx
 * <AddressSuggestionDropdown
 *   suggestions={addressSuggestions}
 *   isVisible={showSuggestions}
 *   isLoading={isSearching}
 *   onClose={() => setShowSuggestions(false)}
 *   onSelectSuggestion={(suggestion) => handleSelectAddress(suggestion)}
 * />
 * ```
 */
export default function AddressSuggestionDropdown({
    suggestions = [],
    isLoading = false,
    onClose,
    onSelectSuggestion,
    isVisible = false,
    position = 'absolute',
    className,
    hintMessage,
    showGoogleAttribution = true,
}: AddressSuggestionDropdownProps): ReactElement | null {
    const { t } = useTranslation('addressSuggestionDropdown');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

    // Filter out "Too many matches" sentinel suggestions; they only carry confidence metadata.
    // Memoized so the keyboard effect below only re-binds when the actual list changes.
    const visibleSuggestions = useMemo(
        () => suggestions.filter((s) => s.confidence !== 'Too many matches'),
        [suggestions]
    );
    const hasSuggestions = visibleSuggestions.length > 0;

    // Active descendant index for keyboard navigation. `-1` means nothing is highlighted yet
    // (the input still owns the visual focus and the next ArrowDown moves to the first option).
    const [activeIndex, setActiveIndex] = useState(-1);

    // Reset the highlight whenever the suggestion list changes — otherwise the index may point
    // at a stale option that no longer exists or matches a different address.
    useEffect(() => {
        setActiveIndex(-1);
    }, [visibleSuggestions]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        if (isVisible) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isVisible, onClose]);

    // Keyboard navigation — combobox pattern. The shopper keeps focus on the address input
    // while arrow keys move the highlight inside the dropdown and Enter commits the selection.
    // The listener is bound to `document` (not the dropdown) because the dropdown itself is
    // never focused — focus stays in the input that triggered it. `preventDefault` on the
    // arrow keys stops the browser from moving the text caret inside the input.
    useEffect(() => {
        if (!isVisible || !hasSuggestions) return undefined;

        const handleKeyDown = (event: KeyboardEvent) => {
            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    setActiveIndex((prev) => (prev + 1) % visibleSuggestions.length);
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    setActiveIndex((prev) => (prev <= 0 ? visibleSuggestions.length - 1 : prev - 1));
                    break;
                case 'Home':
                    event.preventDefault();
                    setActiveIndex(0);
                    break;
                case 'End':
                    event.preventDefault();
                    setActiveIndex(visibleSuggestions.length - 1);
                    break;
                case 'Enter':
                    // Only commit when a suggestion is highlighted — otherwise let the form's
                    // native submit handler take over (the shopper may want to submit with the
                    // raw typed address).
                    if (activeIndex >= 0 && activeIndex < visibleSuggestions.length) {
                        event.preventDefault();
                        onSelectSuggestion(visibleSuggestions[activeIndex]);
                    }
                    break;
                case 'Escape':
                    event.preventDefault();
                    onClose();
                    break;
                case 'Tab':
                    // Don't preventDefault — Tab should advance focus to the next field. Just
                    // dismiss the dropdown so the shopper isn't left with a stale picker open.
                    onClose();
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isVisible, hasSuggestions, visibleSuggestions, activeIndex, onClose, onSelectSuggestion]);

    // Keep the highlighted option scrolled into view when navigating with the keyboard.
    // `scrollIntoView` is guarded because it's missing in JSDOM (unit tests) and in some
    // older mobile browsers where the polyfill hasn't loaded yet.
    useEffect(() => {
        if (activeIndex < 0) return;
        const node = optionRefs.current[activeIndex];
        if (node && typeof node.scrollIntoView === 'function') {
            node.scrollIntoView({ block: 'nearest' });
        }
    }, [activeIndex]);

    if (!isVisible) {
        return null;
    }

    const positionClasses = {
        absolute: 'absolute',
        relative: 'relative',
        fixed: 'fixed',
    };

    if (isLoading) {
        return (
            <Card
                className={cn(
                    positionClasses[position],
                    'top-full left-0 right-0 z-[1000] mt-1 gap-0 py-2',
                    className,
                    'rounded-none shadow-md'
                )}>
                <CardContent className="flex items-center justify-center gap-2 p-2">
                    <Spinner size="sm" />
                    <Typography variant="muted" as="span">
                        {t('loading')}
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    // Show the "No matches" message only when there's truly nothing to show
    // (no suggestions and no hint such as "Continue typing...").
    const showNoMatches = !hasSuggestions && !hintMessage;

    return (
        <Card
            ref={dropdownRef}
            data-testid="address-suggestion-dropdown"
            className={cn(
                positionClasses[position],
                'top-full left-0 right-0 z-[1000] mt-1 gap-0 py-0',
                className,
                'rounded-none shadow-md'
            )}>
            {/* Header — always rendered when visible. The label flips between
                "SUGGESTED" and "NO MATCHES" so the close X stays available in
                both states and keeps the same visual treatment. */}
            <div className="flex flex-row items-center justify-between pl-4 pr-1 py-1">
                <Typography
                    variant="muted"
                    as="span"
                    className="font-medium"
                    data-testid={showNoMatches ? 'address-no-matches' : undefined}>
                    {showNoMatches ? t('noMatches') : t('suggested')}
                </Typography>
                <Button
                    variant="ghost"
                    size="icon"
                    className="size-6"
                    onClick={onClose}
                    aria-label={t('closeSuggestions')}>
                    <X className="size-4 text-muted-foreground" />
                </Button>
            </div>

            {/* Hint message (e.g. "Continue typing..." or "Add a house number") */}
            {hintMessage && (
                <CardContent className="py-2 px-4">
                    <Typography variant="muted" as="span" className="text-sm">
                        {hintMessage}
                    </Typography>
                </CardContent>
            )}

            {/* Suggestions List */}
            {hasSuggestions && (
                <CardContent className="flex flex-col p-0" role="listbox" aria-label={t('suggested')}>
                    {visibleSuggestions.map((suggestion, index) => {
                        const displayText =
                            suggestion.description ||
                            `${suggestion.structured_formatting?.main_text || ''}${suggestion.structured_formatting?.secondary_text ? `, ${suggestion.structured_formatting.secondary_text}` : ''}`.trim() ||
                            '';
                        const isActive = index === activeIndex;

                        return (
                            <Button
                                ref={(node) => {
                                    optionRefs.current[index] = node;
                                }}
                                key={suggestion.place_id || suggestion.global_address_key || index}
                                variant="ghost"
                                role="option"
                                aria-selected={isActive}
                                data-active={isActive ? 'true' : undefined}
                                className={cn(
                                    'w-full justify-start !px-4 py-3 h-auto rounded-none hover:bg-accent',
                                    isActive && 'bg-accent'
                                )}
                                onMouseEnter={() => setActiveIndex(index)}
                                onClick={() => onSelectSuggestion(suggestion)}>
                                <MapPin className="size-4 shrink-0 text-foreground" />
                                <Typography variant="small" as="span" className="truncate font-normal">
                                    {displayText}
                                </Typography>
                            </Button>
                        );
                    })}
                </CardContent>
            )}

            {/* Google Attribution - only show when using Google Places API and we have
                suggestions to attribute. Hidden in the no-matches state per design. */}
            {showGoogleAttribution && hasSuggestions && (
                <CardFooter className="px-4 py-3">
                    <img src={googleMapsLogo} alt="Google Maps" className="w-[98px] h-[18px]" />
                </CardFooter>
            )}
        </Card>
    );
}
